import React from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Users, 
  Zap, 
  Award, 
  DollarSign, 
  Target 
} from 'lucide-react';

const WhyChooseUsSection = () => {
  const reasons = [
    {
      icon: MapPin,
      title: 'London & Nationwide',
      description: 'Operational capability across London and the UK for single and multi-site contracts.',
    },
    {
      icon: Users,
      title: 'Structured Workforce',
      description: 'Trained, vetted teams with documented accountability and performance tracking.',
    },
    {
      icon: Zap,
      title: 'Rapid Deployment',
      description: 'Scalable resource mobilisation for urgent requirements and contract starts.',
    },
    {
      icon: Award,
      title: 'Operational Track Record',
      description: 'Proven delivery across commercial, healthcare, hospitality, and residential sectors.',
    },
    {
      icon: DollarSign,
      title: 'Competitive Structures',
      description: 'Cost-effective service models designed for long-term contract value.',
    },
    {
      icon: Target,
      title: 'Quality Systems',
      description: 'Documented processes, regular audits, and continuous improvement protocols.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section
      id="why-us"
      className="section-padding bg-white relative overflow-hidden"
      data-testid="why-choose-us-section"
    >
      {/* Background decoration */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <motion.span 
            className="font-manrope text-sm font-semibold text-accent uppercase tracking-widest mb-4 block"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            The Hati Miri Advantage
          </motion.span>
          <h2
            className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 uppercase tracking-tight mb-4"
            data-testid="why-us-headline"
          >
            Built for <span className="text-accent">Reliability</span>
          </h2>
          <p className="font-manrope text-lg text-slate-600 max-w-2xl mx-auto">
            Operational excellence, accountable teams, and scalable delivery — the foundation of every partnership.
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group p-8 bg-slate-50 rounded-sm border border-slate-200 hover:border-accent transition-all duration-300 hover:shadow-xl hover:shadow-accent/10"
              data-testid={`why-us-reason-${index}`}
            >
              <motion.div 
                className="w-14 h-14 bg-primary rounded-sm flex items-center justify-center mb-6 group-hover:bg-accent transition-colors duration-300"
                whileHover={{ rotate: 5, scale: 1.1 }}
              >
                <reason.icon className="w-7 h-7 text-white" />
              </motion.div>
              <h3 className="font-oswald text-xl font-bold text-slate-800 uppercase tracking-tight mb-3 group-hover:text-accent transition-colors">
                {reason.title}
              </h3>
              <p className="font-manrope text-sm text-slate-600">
                {reason.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          whileHover={{ scale: 1.01 }}
          className="text-center py-12 px-8 bg-gradient-to-r from-slate-100 via-white to-slate-100 rounded-sm border-2 border-accent relative overflow-hidden"
        >
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-accent/0 via-accent/5 to-accent/0"
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          />
          <blockquote
            className="font-oswald text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800 uppercase tracking-tight relative z-10"
            data-testid="slogan-quote"
          >
            "We don't stop —{' '}
            <span className="text-accent">until the job is done right.</span>"
          </blockquote>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
